import { type CSSProperties } from 'react'
import { cn } from '#app/utils/misc.js'
import { useRevealOnce } from '#app/utils/motion.ts'

/** above this many seats a dot per seat gets too small — draw a bar */
const MAX_DOTS = 60

/**
 * One dot per seat: free seats glow, taken seats are dimmed. The first time
 * the meter scrolls into view the free seats pop in one by one. The text
 * next to it carries the numbers, so the dots are decorative.
 */
export function SeatMeter({
	total,
	taken,
	className,
}: {
	total: number
	taken: number
	className?: string
}) {
	const [ref, reveal] = useRevealOnce<HTMLDivElement>({ threshold: 0.6 })
	const free = Math.max(0, total - taken)
	const low = free > 0 && free <= Math.max(3, Math.round(total * 0.15))
	const label =
		free === 0
			? 'Fullt hus'
			: low
				? `Berre ${free} ${free === 1 ? 'plass' : 'plassar'} igjen`
				: `${free} av ${total} plassar ledige`

	return (
		<div
			ref={ref}
			className={cn('seat-meter', className)}
			data-reveal={reveal}
			data-low={low ? '' : undefined}
			data-full={free === 0 ? '' : undefined}
		>
			{total <= MAX_DOTS ? (
				<span
					className="seat-meter-dots"
					aria-hidden="true"
					// even rows of at most 15 chairs: 20 seats → 2 × 10
					style={
						{
							'--seat-cols': Math.ceil(total / Math.ceil(total / 15)),
						} as CSSProperties
					}
				>
					{Array.from({ length: total }, (_, i) => (
						<span
							key={i}
							className={i < free ? 'seat-dot seat-dot-free' : 'seat-dot'}
							style={{ '--seat-i': i } as CSSProperties}
						/>
					))}
				</span>
			) : (
				<span className="seat-meter-bar" aria-hidden="true">
					<span style={{ '--seat-free': free / total } as CSSProperties} />
				</span>
			)}
			<span className="seat-meter-label">{label}</span>
		</div>
	)
}
